import { isLightColor, resolveDividerColor } from "./luminance";

export interface PortalBackgroundBranding {
  portalBackgroundColor?: string | null;
}

// Semantic success/error tokens that ship green/red text and icons (e.g. the
// "Fast refund" / "Printer-free and box-free" subtitle on shipment method,
// the Zap / Recycle icons beside them, and small red error text). When the
// merchant paints the portal anything other than white/very-light grey,
// those semantic colors risk clashing with the surface — e.g. green text on
// a green background. Redirect them to the neutral primary text/foreground
// tokens.
const SEMANTIC_TEXT_OVERRIDES: Record<string, string> = {
  // Text tokens — used by <Text textColor="success" | "error"> and friends.
  "--redo-colors-text-text-success-primary-600":
    "var(--redo-colors-text-text-primary-900)",
  "--redo-colors-text-text-error-primary-600":
    "var(--redo-colors-text-text-primary-900)",
  // Foreground tokens — used by raw foreground references.
  "--redo-colors-foreground-fg-success-primary":
    "var(--redo-colors-foreground-fg-primary)",
  "--redo-colors-foreground-fg-success-secondary":
    "var(--redo-colors-foreground-fg-secondary)",
  "--redo-colors-foreground-fg-error-primary":
    "var(--redo-colors-foreground-fg-primary)",
  "--redo-colors-foreground-fg-error-secondary":
    "var(--redo-colors-foreground-fg-secondary)",
  // Featured-icon tokens — used by <Icon color="success-solid" | "error-solid">
  // for the inline icons that sit beside the success/error text. Both the
  // "light featured icon" and "dark featured icon" variants point at the
  // same raw success/error scale, so override both so any icon-style consumer
  // is covered.
  "--redo-component-colors-components-icons-featured-icons-light-featured-icon-light-fg-success":
    "var(--redo-colors-foreground-fg-primary)",
  "--redo-component-colors-components-icons-featured-icons-dark-featured-icon-dark-fg-success":
    "var(--redo-colors-foreground-fg-primary)",
  "--redo-component-colors-components-icons-featured-icons-light-featured-icon-light-fg-error":
    "var(--redo-colors-foreground-fg-primary)",
  "--redo-component-colors-components-icons-featured-icons-dark-featured-icon-dark-fg-error":
    "var(--redo-colors-foreground-fg-primary)",
  // Disabled-row background — used by the "already returned" item row on
  // select-items (`<Flex bgColor="disabled">`). The default light-mode token
  // is `gray-light-mode-100` (~#f5f5f5), which reads as a bright off-white
  // patch on dark portal themes. Flatten to transparent so the row blends
  // into the surface and relies on the dimmer text + reason copy as the
  // only "disabled" cue.
  "--redo-colors-background-bg-disabled": "transparent",
  "--redo-colors-background-bg-disabled_subtle": "transparent",
  // Secondary surface — used by <Flex bgColor="secondary"> on the orders
  // list (order header bar, "Your return was submitted" status banners). The
  // default token is `gray-light-mode-50`, which reads bright on dark
  // themes. Flatten to transparent so these strips blend into the dark
  // portal background; the border-secondary override above gives them a
  // visible boundary.
  "--redo-colors-background-bg-secondary": "transparent",
};

export function buildPortalBackgroundCssRules(
  root: string,
  branding: PortalBackgroundBranding | null | undefined,
  { modalless = false }: { modalless?: boolean } = {},
): string[] {
  const raw = branding?.portalBackgroundColor;
  const color = typeof raw === "string" && raw.length > 0 ? raw : undefined;
  if (color === undefined) {
    return [];
  }

  // Modalless pages layer white cards over a bg-secondary page surface: paint
  // only that surface and skip the token flips so cards/modals stay default.
  if (modalless) {
    return [
      `[data-ui="select-items"] { background-color: ${color}; }`,
      `[data-ui="orderDetails"] { background-color: ${color}; }`,
      `[data-modalless-page] { background-color: ${color}; }`,
    ];
  }

  // The themed scope: the customer-portal root AND modal/drawer surfaces
  // that render via React portals at <body> — outside the customer-portal
  // DOM subtree. Defaulting `data-ui="portal-modal"` on RedoBaseModal /
  // RedoDrawer means every modal in the app gets this scope automatically.
  // Anything that escapes both — tooltips, popper-rendered dropdowns —
  // either has its own carve-out (e.g. [data-redo-dropdown]) or keeps the
  // design-system defaults (typically the right behavior for transient UI).
  const scopes = `${root}, [data-ui="portal-modal"]`;

  const rules: string[] = [];

  // Redirect the design-system "primary background" token to the merchant's
  // portal background. Every <Flex bgColor="primary"> / <Card> across the
  // customer-portal reads this token, so flipping it on the themed scope
  // means every nested surface adopts the portal color automatically — no
  // per-page data-ui allowlist required. Carve-outs (e.g. the dropdown
  // popover, which pins itself white) win on cascade because they're later
  // descendant rules at the same specificity tier.
  //
  // `!important` defends against MUI Paper and other consumers that bake
  // the design-system token resolution into an emotion-generated class.
  rules.push(
    `${scopes} { --redo-colors-background-bg-primary: ${color} !important; }`,
  );

  // MUI's Drawer Paper hardcodes its own `background-color: #fff` via an
  // emotion-generated class, ignoring the `--redo-colors-background-bg-
  // primary` token flip above. Paint the modal surface directly so the
  // cart drawer / portal modal pick up the merchant color.
  rules.push(`[data-ui="portal-modal"] { background-color: ${color}; }`);
  rules.push(`[data-ui="select-items"] { background-color: ${color}; }`);
  rules.push(`[data-ui="orderDetails"] { background-color: ${color}; }`);
  rules.push(`[data-modalless-page] { background-color: ${color}; }`);

  // Pin the inheriting `color` so MUI Paper-style elements (e.g. the cart
  // drawer) that set their own default color don't override the merchant's
  // primary text.
  rules.push(`${scopes} { color: var(--redo-colors-text-text-primary-900); }`);

  // Auto-flip dividers and borders so they contrast with the surface. Both
  // the "primary" border token (used by <Divider/> and bgColor-driven
  // borders) and the "secondary" token (used by design-system Card and the
  // item-row separators on the select-items page) need to flip.
  //
  // `!important` is needed because the design system declares these tokens
  // on `:root` and on the `.light-mode` class — and intermediate elements
  // (e.g. RedoBadge) carry `.light-mode`, which would otherwise win the
  // cascade for descendants of those nodes.
  const dividerColor = resolveDividerColor(color);
  if (dividerColor !== undefined) {
    rules.push(
      `${scopes} { --redo-colors-border-border-primary: ${dividerColor} !important; --redo-colors-border-border-secondary: ${dividerColor} !important; }`,
    );
  }

  // Flatten semantic success/error text to the neutral primary text color
  // unless the merchant's chosen background is white-ish (luminance ≥ 0.85),
  // where the default semantic colors still read fine.
  if (isLightColor(color) === false) {
    const overrides = Object.entries(SEMANTIC_TEXT_OVERRIDES)
      .map(([token, value]) => `${token}: ${value};`)
      .join(" ");
    rules.push(`${scopes} { ${overrides} }`);
  }

  return rules;
}
